import type { ReactNode } from 'react'
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { noop, useQueryClient } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { trpc } from '@/main'
import { useSessionUser } from '@/hooks/use-session'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { SettingsShell } from './-components/settings/settings-shell'
import { AdminBreadcrumbs, AdminSidebar } from './-components/admin/admin-nav'
import { AdminPending } from './-components/admin/admin-pending'
import { isSuperAdminPage } from './-components/admin/admin-pages'

/**
 * Layout for the super-admin dev playground, in the account's design (account.tsx): the title bar's trail, the tree
 * of its pages beside them (the phone reaches it from the trail's last crumb), and each page in the reading width.
 * The bare `/admin` redirects to the metrics. The playground is open to any signed-in user; its other pages, whose
 * data is SUPER_ADMIN_ONLY, show anyone else a "No access" panel in place of the page.
 */
export const Route = createFileRoute('/_app/admin')({
  // The tree lists the email templates under their page, so their render-free index is read on every page here — by a
  // super admin only, as the templates are SUPER_ADMIN_ONLY.
  context: () => ({ emailTemplatesQueryOptions: trpc.admin.listEmailTemplates.queryOptions() }),
  loader: ({ context }) => {
    if (context.queryClient.getQueryData(context.sessionUserQueryOptions.queryKey)?.canDevLoginAs)
      context.queryClient.query(context.emailTemplatesQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: AdminPending,
})

function RouteComponent() {
  return (
    <SettingsShell
      icon={NAVIGATION_ICONS.admin}
      title="Admin"
      breadcrumbs={<AdminBreadcrumbs />}
      sidebar={(className) => <AdminSidebar className={className} />}
      contentTestId="admin-content"
    >
      <SuperAdminGate>
        <Outlet />
      </SuperAdminGate>
    </SettingsShell>
  )
}

/** Above the outlet, because each page's data streams in a match of its own whose boundary no wrapper here sees. */
function SuperAdminGate(props: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const superAdmin = !!useSessionUser()?.canDevLoginAs
  const gated = useLocation({ select: (location) => isSuperAdminPage(location.pathname) })
  if (gated && !superAdmin) return <NoAccess />
  // A document rendered without the marker cookie streams its refused reads into this cache, and a page mounting on
  // them rethrows the refusal as its own crash: once the session says super admin, they are asked again instead.
  if (gated) queryClient.removeQueries({ predicate: (query) => query.state.error?.message === 'SUPER_ADMIN_ONLY' })
  return props.children
}

function NoAccess() {
  const { t } = useLingui()
  return (
    <PanelEmptyState
      icon={<NAVIGATION_ICONS.admin className="text-muted-foreground size-5" />}
      title={t`No access`}
      description={t`You don't have permission to access this section`}
    />
  )
}
